import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Upload, Button, Progress, message, Card, Space, Typography, List, Tag, Switch } from 'antd';
import { UploadOutlined, FileOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import OSS from 'ali-oss';

const { Text, Title } = Typography;

/**
 * OSS文件上传组件
 * 支持前端直传阿里云OSS，带队列控制
 */
const OSSFileUploader = ({ 
  folder = 'uploads', 
  onUploadSuccess, 
  onUploadError,
  maxFileSize = 5 * 1024 * 1024 * 1024, // 5GB
  accept = '*/*',
  multiple = false,
  showProgress = true,
  title = '文件上传',
  showUploadList = true,
  maxConcurrentUploads = 3, // 最大同时上传数量
  enableQueue = true // 是否启用队列控制
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadTasks, setUploadTasks] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]); // 上传队列
  const [activeUploads, setActiveUploads] = useState(0); // 当前活跃上传数
  const [queueEnabled, setQueueEnabled] = useState(enableQueue);
  const fileInputRef = useRef(null);

  /**
   * 获取STS临时凭证
   */
  const getSTSToken = async (filename) => {
    try {
      const response = await axios.post('/api/oss/sts-token/', {
        filename,
        folder
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || '获取STS凭证失败');
      }
    } catch (error) {
      console.error('获取STS凭证失败:', error);
      throw error;
    }
  };

  /**
   * 初始化OSS客户端
   */
  const initOSSClient = async (stsData) => {
    const config = {
      accessKeyId: stsData.access_key_id,
      accessKeySecret: stsData.access_key_secret,
      bucket: stsData.bucket,
      endpoint: stsData.endpoint,
      secure: true
    };

    // 如果是STS模式，添加stsToken；如果是直接访问模式，不添加stsToken
    if (stsData.security_token && stsData.mode !== 'direct') {
      config.stsToken = stsData.security_token;
    }

    const ossClient = new OSS(config);
    return ossClient;
  };

  /**
   * 上传文件到OSS
   */
  const uploadToOSS = async (file, stsData, taskId) => {
    const ossClient = await initOSSClient(stsData);
    
    // 记录上传开始时间
    const startTime = Date.now();
    let lastUpdateTime = startTime;
    let lastUploadedBytes = 0;
    let totalUploadedBytes = 0;
    
    const result = await ossClient.multipartUpload(stsData.file_path, file, {
      progress: (p, checkpoint) => {
        const currentTime = Date.now();
        
        // 更精确的已上传字节数计算
        let currentUploadedBytes = 0;
        
        // 优先使用 checkpoint 中的分片信息计算已上传字节数
        if (checkpoint && checkpoint.uploadId && checkpoint.parts) {
          const uploadedParts = checkpoint.parts.length;
          const partSize = 1024 * 1024; // 1MB 分片大小
          const uploadedBytesFromParts = uploadedParts * partSize;
          
          // 使用分片信息计算已上传字节数
          currentUploadedBytes = Math.min(uploadedBytesFromParts, file.size);
          totalUploadedBytes = currentUploadedBytes;
        } else {
          // 如果没有 checkpoint 信息，使用进度百分比计算
          currentUploadedBytes = Math.round(p * file.size);
          totalUploadedBytes = currentUploadedBytes;
        }
        
        // 确保已上传字节数不超过文件总大小
        currentUploadedBytes = Math.min(currentUploadedBytes, file.size);

        // 计算上传速度
        const timeDiff = (currentTime - lastUpdateTime) / 1000; // 秒
        const bytesDiff = currentUploadedBytes - lastUploadedBytes;
        const speed = timeDiff > 0 ? bytesDiff / timeDiff : 0;

        // 更新任务进度
        setUploadTasks(prev => prev.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                progress: Math.round(p * 100),
                uploadedBytes: currentUploadedBytes,
                speed: speed,
                status: 'uploading'
              }
            : task
        ));

        // 调试日志：输出进度信息
        if (taskId % 1000 < 10) { // 只对部分任务输出日志，避免过多日志
          console.log('上传进度调试:', {
            taskId,
            progress: Math.round(p * 100),
            uploadedBytes: currentUploadedBytes,
            totalBytes: file.size,
            checkpoint: checkpoint ? {
              uploadId: checkpoint.uploadId,
              partsCount: checkpoint.parts ? checkpoint.parts.length : 0
            } : null
          });
        }

        lastUpdateTime = currentTime;
        lastUploadedBytes = currentUploadedBytes;
      }
    });

    return result;
  };

  /**
   * 保存文件信息到后端
   */
  const saveFileInfo = async (file, stsData, ossResult) => {
    try {
      const response = await axios.post('/api/oss/upload-callback/', {
        file_path: stsData.file_path,
        original_filename: file.name,
        file_size: file.size,
        content_type: file.type,
        etag: ossResult.etag
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || '保存文件信息失败');
      }
    } catch (error) {
      console.error('保存文件信息失败:', error);
      throw error;
    }
  };

  /**
   * 处理单个文件上传
   */
  const processFileUpload = useCallback(async (file, taskId) => {
    try {
      // 1. 获取STS临时凭证
      const stsData = await getSTSToken(file.name);
      
      // 2. 上传文件到OSS
      const ossResult = await uploadToOSS(file, stsData, taskId);
      
      // 3. 保存文件信息到后端
      const fileInfo = await saveFileInfo(file, stsData, ossResult);
      
      // 4. 添加到已上传文件列表
      const newFile = {
        ...fileInfo,
        originalName: file.name,
        uploadTime: new Date().toISOString()
      };
      setUploadedFiles(prev => [...prev, newFile]);

      // 5. 从任务列表中删除已成功的任务
      setUploadTasks(prev => prev.filter(task => task.id !== taskId));

      // 6. 调用成功回调
      if (onUploadSuccess) {
        onUploadSuccess(newFile);
      }

      message.success(`${file.name} 上传成功`);
    } catch (error) {
      console.error('上传失败:', error);
      
      // 更新任务状态为失败
      setUploadTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, status: 'error', error: error.message }
          : task
      ));

      // 调用错误回调
      if (onUploadError) {
        onUploadError(error, file);
      }

      message.error(`${file.name} 上传失败: ${error.message}`);
    } finally {
      // 减少活跃上传数
      setActiveUploads(prev => prev - 1);
      
      // 处理队列中的下一个文件
      processQueue();
    }
  }, [onUploadSuccess, onUploadError]);

  /**
   * 处理上传队列
   */
  const processQueue = useCallback(() => {
    if (!queueEnabled || activeUploads >= maxConcurrentUploads) {
      return;
    }

    setUploadQueue(prev => {
      const newQueue = [...prev];
      const availableSlots = maxConcurrentUploads - activeUploads;
      const filesToProcess = newQueue.splice(0, availableSlots);
      
      // 启动新的上传任务
      filesToProcess.forEach(({ file, taskId }) => {
        setActiveUploads(prev => prev + 1);
        processFileUpload(file, taskId);
      });
      
      return newQueue;
    });
  }, [queueEnabled, activeUploads, maxConcurrentUploads, processFileUpload]);

  /**
   * 处理文件上传
   */
  const handleFileUpload = useCallback(async (file) => {
    // 检查文件大小
    if (file.size > maxFileSize) {
      message.error(`文件大小不能超过 ${(maxFileSize / 1024 / 1024 / 1024).toFixed(1)}GB`);
      return;
    }

    // 生成任务ID
    const taskId = Date.now() + Math.random();
    
    // 创建上传任务
    const uploadTask = {
      id: taskId,
      file,
      status: 'waiting', // waiting, uploading, success, error
      progress: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
      speed: 0,
      startTime: Date.now(),
      error: null
    };

    // 添加到上传任务列表
    setUploadTasks(prev => [...prev, uploadTask]);
    setUploading(true);

    // 如果启用队列且当前活跃上传数达到上限，加入队列
    if (queueEnabled && activeUploads >= maxConcurrentUploads) {
      setUploadQueue(prev => [...prev, { file, taskId }]);
      // 更新任务状态为等待中
      setUploadTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, status: 'waiting' }
          : task
      ));
    } else {
      // 直接开始上传
      setActiveUploads(prev => prev + 1);
      processFileUpload(file, taskId);
    }
  }, [maxFileSize, queueEnabled, activeUploads, maxConcurrentUploads, processFileUpload]);

  /**
   * 处理文件选择
   */
  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    
    if (multiple) {
      files.forEach(file => handleFileUpload(file));
    } else {
      handleFileUpload(files[0]);
    }
    
    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  }, [multiple, handleFileUpload]);

  /**
   * 删除已上传文件
   */
  const handleDeleteFile = useCallback((fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  /**
   * 删除上传任务
   */
  const handleDeleteTask = useCallback((taskId) => {
    setUploadTasks(prev => prev.filter(task => task.id !== taskId));
    // 从队列中移除
    setUploadQueue(prev => prev.filter(item => item.taskId !== taskId));
  }, []);

  /**
   * 清空队列
   */
  const handleClearQueue = useCallback(() => {
    setUploadQueue([]);
    setUploadTasks(prev => prev.filter(task => task.status !== 'waiting'));
  }, []);

  /**
   * 格式化文件大小
   */
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * 格式化上传速度
   */
  const formatSpeed = useCallback((bytesPerSecond) => {
    if (bytesPerSecond === 0) return '0 B/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * 获取状态图标
   */
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'waiting':
        return <PauseCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <FileOutlined />;
    }
  }, []);

  /**
   * 获取状态标签
   */
  const getStatusTag = useCallback((status) => {
    switch (status) {
      case 'waiting':
        return <Tag color="warning">等待中</Tag>;
      case 'uploading':
        return <Tag color="processing">上传中</Tag>;
      case 'success':
        return <Tag color="success">上传成功</Tag>;
      case 'error':
        return <Tag color="error">上传失败</Tag>;
      default:
        return <Tag>未知状态</Tag>;
    }
  }, []);

  // 计算队列统计信息
  const queueStats = useMemo(() => {
    const waiting = uploadTasks.filter(task => task.status === 'waiting').length;
    const uploading = uploadTasks.filter(task => task.status === 'uploading').length;
    const success = uploadTasks.filter(task => task.status === 'success').length;
    const error = uploadTasks.filter(task => task.status === 'error').length;
    
    return { waiting, uploading, success, error };
  }, [uploadTasks]);

  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* 队列控制开关 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Text>队列控制:</Text>
            <Switch 
              checked={queueEnabled} 
              onChange={setQueueEnabled}
              checkedChildren="开启"
              unCheckedChildren="关闭"
            />
          </Space>
          {queueEnabled && (
            <Space>
              <Text type="secondary">
                活跃: {activeUploads}/{maxConcurrentUploads} | 
                等待: {queueStats.waiting} | 
                成功: {queueStats.success} | 
                失败: {queueStats.error}
              </Text>
              {uploadQueue.length > 0 && (
                <Button size="small" onClick={handleClearQueue}>
                  清空队列
                </Button>
              )}
            </Space>
          )}
        </div>

        {/* 文件选择按钮 */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={useCallback(() => fileInputRef.current?.click(), [])}
            loading={uploading}
            disabled={uploading}
          >
            选择文件
          </Button>
          <Text type="secondary" style={{ marginLeft: 8 }}>
            支持文件大小最大 {useMemo(() => formatFileSize(maxFileSize), [maxFileSize, formatFileSize])}
          </Text>
        </div>

        {/* 上传任务列表 */}
        {uploadTasks.length > 0 && showUploadList && (
          <div>
            <Title level={5}>上传任务</Title>
            <List
              dataSource={uploadTasks}
              renderItem={(task) => (
                <List.Item
                  actions={[
                    <DeleteOutlined
                      key="delete"
                      onClick={() => handleDeleteTask(task.id)}
                      style={{ color: '#ff4d4f' }}
                    />
                  ]}
                >
                  <List.Item.Meta
                    avatar={getStatusIcon(task.status)}
                    title={
                      <Space>
                        <Text strong>{task.file.name}</Text>
                        {getStatusTag(task.status)}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Text type="secondary">
                          文件大小: {formatFileSize(task.file.size)}
                        </Text>
                        {task.status === 'uploading' && (
                          <>
                            <Text type="secondary">
                              上传速度: {formatSpeed(task.speed)}
                            </Text>
                            <Progress 
                              percent={task.progress} 
                              status="active" 
                              size="small"
                              style={{ width: 200 }}
                              format={(percent) => `${percent}%`}
                            />
                            <Text type="secondary">
                              {formatFileSize(task.uploadedBytes)} / {formatFileSize(task.totalBytes)}
                            </Text>
                          </>
                        )}
                        {task.status === 'error' && (
                          <Text type="danger">{task.error}</Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {/* 已上传文件列表 */}
        {uploadedFiles.length > 0 && showUploadList && (
          <div>
            <Title level={5}>已上传文件</Title>
            <List
              dataSource={uploadedFiles}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <DeleteOutlined
                      key="delete"
                      onClick={() => handleDeleteFile(file.id)}
                      style={{ color: '#ff4d4f' }}
                    />
                  ]}
                >
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title={
                      <Space>
                        <Text strong>{file.originalName}</Text>
                        <Tag color="success">已上传</Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical">
                        <Text type="secondary">
                          大小: {formatFileSize(file.file_size)} | 
                          上传时间: {new Date(file.uploadTime).toLocaleString()}
                        </Text>
                        {file.full_url && (
                          <Text type="secondary">
                            <a href={file.full_url} target="_blank" rel="noopener noreferrer">
                              查看文件
                            </a>
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default OSSFileUploader;
